// Lazy-loaded ffmpeg.wasm client used to do real, frame-level video editing
// (trim, per-clip audio volume/mute/voiceover mix, multi-clip concatenation)
// entirely in the browser — there is no media-processing backend to call.
//
// IMPORTANT: the @ffmpeg/ffmpeg npm package is NOT imported as an ES module
// here — it's loaded at runtime as a plain <script> tag instead, exactly
// like pulling in a CDN <script> in index.html. Two things ruled out
// importing it directly into our own webpack bundle:
//   1. @ffmpeg/ffmpeg 0.12+'s bundle uses native JS private class fields
//      (`#field`), a syntax level this app's build (react-scripts 4 /
//      webpack 4, via craco) cannot parse ("Unexpected character '#'").
//   2. @ffmpeg/ffmpeg 0.11.x's package.json "main" points at its raw,
//      unbundled ESM source (not a pre-built dist file), and that source
//      uses `import.meta.url` — again unsupported by webpack 4
//      ("Unexpected token" at `new URL(_data, import.meta.url)`).
// Loading `dist/ffmpeg.min.js` as a <script> tag sidesteps both: it's
// already a self-contained, pre-bundled UMD build (built by the package's
// own webpack 5 config) that defines `window.FFmpeg = { createFFmpeg,
// fetchFile, ... }` as a global, so our bundler never has to parse the
// package's source at all.
//
// Core build: @ffmpeg/core-st ("st" = single-thread), not plain
// @ffmpeg/core. Plain @ffmpeg/core's wasm binary is built with pthreads and
// needs a real SharedArrayBuffer — which only exists when the page is
// served with Cross-Origin-Opener-Policy/Cross-Origin-Embedder-Policy
// response headers (COOP/COEP). A plain CRA/craco dev server (and most
// prod static hosts, unless deliberately configured) sends neither, so
// `new SharedArrayBuffer(...)` throws "SharedArrayBuffer is not defined".
// @ffmpeg/core-st is the same ffmpeg build compiled WITHOUT threads, so it
// only needs a plain ArrayBuffer and works with zero server/webpack config.
//
// Both the library script and the wasm core (ffmpeg-core.js + .wasm +
// .worker.js) are loaded straight from the unpkg CDN, only the first time
// someone actually opens the recorder, and cached for the rest of the
// session.
//
// NOTE: this app does not ship a self-hosted copy under /ffmpeg/ in
// public/ — an earlier version of this file tried to auto-detect one first
// (a HEAD request to e.g. /ffmpeg/ffmpeg-core.js, falling back to the CDN
// if that 404'd), but that probe itself was the bug: with no such folder
// present, every single export attempt spent a request hitting a guaranteed
// 404 first. If you want to self-host instead of relying on the CDN
// (e.g. for offline use, or to avoid depending on unpkg's availability),
// copy node_modules/@ffmpeg/ffmpeg/dist/* (ffmpeg.min.js AND its sibling
// chunk file, e.g. 046d0074eee1d99a674a.js — it's fetched relative to
// ffmpeg.min.js's own URL at runtime) and node_modules/@ffmpeg/core-st/dist/*
// into public/ffmpeg/, then point LIB_URL/CORE_BASE below at that folder
// instead of the CDN — don't reintroduce a runtime probe for it.

const FFMPEG_LIB_VERSION = '0.11.6'
// 0.11.0 on npm is missing dist/ffmpeg-core.worker.js entirely (a packaging
// gap in that release — confirmed against the published tarball), which the
// loader always fetches regardless of single- vs multi-threaded core, so it
// 404s (and unpkg reports a missing file as a CORS failure alongside the
// 404, which is what actually shows up in the console). 0.11.1 ships that
// file, so pin to that instead.
const CORE_VERSION = '0.11.1'
const CORE_BASE = `https://unpkg.com/@ffmpeg/core-st@${CORE_VERSION}/dist`
const LIB_URL = `https://unpkg.com/@ffmpeg/ffmpeg@${FFMPEG_LIB_VERSION}/dist/ffmpeg.min.js`

let instance = null
let loadPromise = null
let libPromise = null
let fetchFileRef = null // set once the library script has loaded

const loadScript = (src) =>
  new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[data-ffmpeg-lib="${src}"]`)
    if (existing) {
      existing.addEventListener('load', () => resolve())
      existing.addEventListener('error', () => reject(new Error(`Failed to load ${src}`)))
      return
    }
    const script = document.createElement('script')
    script.src = src
    script.async = true
    script.dataset.ffmpegLib = src
    script.onload = () => resolve()
    script.onerror = () => reject(new Error(`Failed to load ${src}`))
    document.head.appendChild(script)
  })

/**
 * Ensures window.FFmpeg (the createFFmpeg/fetchFile UMD global) is loaded,
 * loading the library script on first call only.
 */
const ensureFFmpegLib = async () => {
  if (window.FFmpeg?.createFFmpeg) return window.FFmpeg
  if (libPromise) return libPromise

  libPromise = (async () => {
    await loadScript(LIB_URL)
    if (!window.FFmpeg?.createFFmpeg) {
      throw new Error('ffmpeg.wasm library script loaded but did not define window.FFmpeg as expected.')
    }
    return window.FFmpeg
  })().catch((err) => {
    libPromise = null // allow retrying on the next call instead of caching a rejected load
    throw err
  })

  return libPromise
}

/**
 * Returns a ready-to-use FFmpeg instance, loading the library script and the
 * wasm core on first call only. `onProgress(0..1)` is invoked during every
 * subsequent `run()` call (the core reports progress per-operation, not for
 * the initial download).
 */
export const getFFmpeg = async (onProgress) => {
  if (instance) return instance
  if (loadPromise) return loadPromise

  loadPromise = (async () => {
    const { createFFmpeg, fetchFile } = await ensureFFmpegLib()
    fetchFileRef = fetchFile

    const ffmpeg = createFFmpeg({
      log: false,
      corePath: `${CORE_BASE}/ffmpeg-core.js`,
      // createFFmpeg() calls Core.cwrap(options.mainName || 'proxy_main', ...)
      // to get its entry point. 'proxy_main' only exists in the
      // multi-threaded @ffmpeg/core build (compiled with PROXY_TO_PTHREAD);
      // the single-threaded @ffmpeg/core-st build we use here (see the note
      // above on why) exports its entry point as plain 'main' instead.
      // Without this override, cwrap() resolves to a function that doesn't
      // exist in the wasm module, and the first run() call crashes with an
      // opaque "Aborted(), assertion failed, ... proxy_main ..." error
      // instead of a clear one.
      mainName: 'main',
    })
    if (onProgress) {
      ffmpeg.setProgress(({ ratio }) => {
        if (Number.isFinite(ratio)) onProgress(Math.min(1, Math.max(0, ratio)))
      })
    }
    await ffmpeg.load()

    instance = ffmpeg
    return ffmpeg
  })().catch((err) => {
    loadPromise = null // allow retrying on the next call instead of caching a rejected load
    throw err
  })

  return loadPromise
}

const runQuiet = async (ffmpeg, args) => {
  await ffmpeg.run(...args)
}

/**
 * Trims [inSec, outSec) out of `blob` and applies the given audio treatment,
 * writing the result to `outName` inside ffmpeg's virtual filesystem.
 *
 * Takes the clip object itself (not a nested `audio` sub-object — clips only
 * ever carry flat fields: `muted`, `volume`, `voiceoverBlob`, `voiceoverMode`,
 * `voiceoverVolume`, `hasAudio`). An earlier version of this function
 * destructured a non-existent `audio` field off the clip, which meant it was
 * always `undefined` and every mute/volume/voiceover edit was silently
 * dropped at export time — trimming still worked, but nothing else the user
 * did in the clip editor ever made it into the exported file.
 */
const processClip = async (ffmpeg, clip, index) => {
  const { blob, inSec, outSec, muted, volume, voiceoverBlob, voiceoverMode, voiceoverVolume, hasAudio, name } = clip

  const srcName = `src_${index}.input`
  ffmpeg.FS('writeFile', srcName, await fetchFileRef(blob))

  const duration = Math.max(0.1, outSec - inSec)
  const args = ['-ss', String(inSec), '-i', srcName]

  let voiceoverName = null
  if (voiceoverBlob) {
    voiceoverName = `voice_${index}.input`
    ffmpeg.FS('writeFile', voiceoverName, await fetchFileRef(voiceoverBlob))
    args.push('-i', voiceoverName)
  }

  // `-t` is pushed here, after every `-i` has already been added, so ffmpeg
  // parses it as an OUTPUT option that caps the length of the final muxed
  // file. Putting it any earlier (e.g. between the source `-i` and a
  // voiceover `-i`, as this used to do) makes ffmpeg silently bind it to
  // whichever input comes next instead of the output — the source clip's
  // video would then play out to its full remaining length instead of being
  // trimmed to `outSec`, which is what made exports with a voiceover attached
  // come out far longer than the trimmed preview and fail to concatenate
  // cleanly with the other clips.
  args.push('-t', String(duration))

  const outName = `clip_${index}.webm`
  // Screen-only captures with no system audio and no mic never get an audio
  // track at all (see useScreenRecorder) — `hasAudio` records that so we
  // never build a filter that references an `0:a` stream that doesn't exist,
  // which ffmpeg rejects outright. Older clips created before this field
  // existed don't have it; treat that as "has audio" rather than guessing wrong.
  const hasOriginalAudio = hasAudio !== false

  if (muted && !voiceoverName) {
    args.push('-map', '0:v:0', '-an', '-c:v', 'copy')
  } else if (voiceoverName && voiceoverMode === 'replace') {
    args.push('-map', '0:v:0', '-map', '1:a:0', '-c:v', 'copy', '-c:a', 'libopus')
  } else if (voiceoverName && hasOriginalAudio) {
    // overlay: mix original (optionally muted-out) audio with the voiceover
    const srcVol = muted ? 0 : (volume ?? 1)
    const filter = `[0:a]volume=${srcVol}[a0];[1:a]volume=${voiceoverVolume ?? 1}[a1];[a0][a1]amix=inputs=2:duration=longest:dropout_transition=0[aout]`
    args.push('-filter_complex', filter, '-map', '0:v:0', '-map', '[aout]', '-c:v', 'copy', '-c:a', 'libopus')
  } else if (voiceoverName) {
    // "Mix with original" was requested but this clip has no original audio
    // track to mix with — fall back to just using the voiceover, the same
    // as "replace", instead of throwing on a missing `0:a` stream.
    args.push('-map', '0:v:0', '-map', '1:a:0', '-c:v', 'copy', '-c:a', 'libopus')
  } else if (hasOriginalAudio && volume != null && volume !== 1) {
    args.push('-map', '0:v:0', '-map', '0:a:0?', '-af', `volume=${volume}`, '-c:v', 'copy', '-c:a', 'libopus')
  } else {
    args.push('-c', 'copy')
  }

  args.push(outName)
  try {
    await runQuiet(ffmpeg, args)
  } catch (err) {
    throw new Error(`Could not process clip ${index + 1}${name ? ` ("${name}")` : ''} — ${err?.message || 'the video engine rejected this edit.'}`)
  }

  ffmpeg.FS('unlink', srcName)
  if (voiceoverName) ffmpeg.FS('unlink', voiceoverName)

  return outName
}

/**
 * Trims + edits every clip in `clips` (already in the desired final order)
 * and concatenates them into a single webm file, returned as a Blob.
 *
 * clips: [{ blob, inSec, outSec, muted, volume, voiceoverBlob, voiceoverMode, voiceoverVolume }]
 */
export const exportClips = async (clips, { onProgress, onStage } = {}) => {
  if (!clips.length) throw new Error('Nothing to export — record at least one clip first.')

  const ffmpeg = await getFFmpeg((p) => onProgress && onProgress(p * 0.5)) // first half of the bar = engine work

  // Fast path: a single, untouched clip needs no ffmpeg pass at all — but
  // only when `blob` is an actual Blob/File we can hand straight back. A
  // clip seeded from an existing recording's URL (re-opening an
  // already-published video to edit it — see ScreenRecordingStudio's
  // `initialClipUrl`) has a plain URL string in `blob` instead; returning
  // that string as if it were the finished Blob would silently produce a
  // "video" whose contents are just the URL text, so those always go
  // through the real ffmpeg pass below (still cheap: just a `-c copy` remux).
  const isUntouched = (c) =>
    c.blob instanceof Blob &&
    c.inSec <= 0.05 && Math.abs(c.outSec - c.durationSec) < 0.05 &&
    !c.muted && (c.volume == null || c.volume === 1) && !c.voiceoverBlob

  if (clips.length === 1 && isUntouched(clips[0])) {
    onProgress && onProgress(1)
    return clips[0].blob
  }

  const outputNames = []
  for (let i = 0; i < clips.length; i++) {
    onStage && onStage(`Processing clip ${i + 1} of ${clips.length}…`)
    const name = await processClip(ffmpeg, clips[i], i)
    outputNames.push(name)
    onProgress && onProgress(0.5 + 0.4 * ((i + 1) / clips.length))
  }

  let finalName = outputNames[0]

  if (outputNames.length > 1) {
    onStage && onStage('Merging clips…')
    const listContent = outputNames.map((n) => `file '${n}'`).join('\n')
    ffmpeg.FS('writeFile', 'concat_list.txt', new TextEncoder().encode(listContent))
    finalName = 'final_output.webm'
    try {
      await runQuiet(ffmpeg, ['-f', 'concat', '-safe', '0', '-i', 'concat_list.txt', '-c', 'copy', finalName])
    } catch (err) {
      throw new Error(
        'Could not merge the edited clips into one file (this can happen if clips end up with mismatched ' +
        'audio/video formats after editing). Try exporting with fewer edits, or export clips separately.'
      )
    }
  }

  onStage && onStage('Finishing up…')
  const data = ffmpeg.FS('readFile', finalName)
  const outBlob = new Blob([data.buffer], { type: 'video/webm' })

  // Clean up the virtual FS so a later export in the same session isn't
  // slowed down by accumulated files.
  for (const n of outputNames) {
    try { ffmpeg.FS('unlink', n) } catch { /* noop */ }
  }
  if (outputNames.length > 1) {
    try { ffmpeg.FS('unlink', 'concat_list.txt') } catch { /* noop */ }
    try { ffmpeg.FS('unlink', finalName) } catch { /* noop */ }
  }

  onProgress && onProgress(1)
  return outBlob
}
